import { useState, ReactElement } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"

import { Blockquote, Loading, Pagination, Search, Button as WFPButton } from "@wfp/ui"
import { iconSearch } from "@wfp/icons"

import { Admin2Unit, SimpleSelect } from "interfaces/dataInterfaces"

import { useGetAdmin2Query } from "services/core/geoBoundaries"

import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"

const AdminUnit2 = (): ReactElement => {
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(20)
  const [adm0Code, setAdm0Code] = useState<number>(0)
  const [query, setQuery] = useState<string>("")
  const [search, setSearch] = useState<string>("")

  const { data: adminUnitData, isFetching } = useGetAdmin2Query(
    {
      adm0Code,
      search: query,
      skip: page === 1 ? 0 : (page - 1) * pageSize,
      take: pageSize,
    },
    { skip: !adm0Code },
  )

  const handleSearch = (value: string): void => {
    setPage(1)
    setQuery(value)
  }

  const handleCountry = (item?: SimpleSelect): void => {
    setPage(1)
    setAdm0Code(item?.value ?? 0)
  }

  const columns: Array<ColumnShape<Admin2Unit>> = [
    {
      key: "adm2Code",
      dataKey: "adm2Code",
      title: "ID",
      width: 150,
      maxWidth: 150,
    },
    {
      key: "adm1Name",
      dataKey: "adm1Name",
      title: "Admin 1 Name",
      width: 300,
      flexGrow: 1,
    },
    {
      key: "adm2Name",
      dataKey: "adm2Name",
      title: "Admin 2 Name",
      width: 400,
      flexGrow: 1,
    },
    {
      key: "adm2NameLocalized",
      dataKey: "adm2NameLocalized",
      title: "Admin 2 Local Name",
      width: 400,
      flexGrow: 1,
    },
  ]

  return (
    <>
      <HeaderFilters>
        <div className="flex items-center xl:w-10/12 w-full gap-4">
          <CountrySelector allowAll={false} onSelect={handleCountry} isClearable={false} className="xl:w-4/12 w-6/12" />
          <div className="flex items-center">
            <Search
              id="query"
              name="query"
              placeholder="Type here to filter results..."
              className="text-input mr-1"
              value={search ?? ""}
              onChange={item => {
                setSearch(item as unknown as string)
                if (!item) handleSearch("")
              }}
            />

            <WFPButton
              onClick={() => handleSearch(search ?? "")}
              disabled={!adm0Code || !search}
              icon={iconSearch}
              kind="primary"
              iconReverse
            >
              Search
            </WFPButton>
          </div>
        </div>
      </HeaderFilters>

      {!adm0Code && (
        <Blockquote withIcon className="my-8">
          <p className="py-1">Please select a country to view administrative units.</p>
        </Blockquote>
      )}

      {!!adm0Code && isFetching && (
        <div className="w-full h-[200px] flex items-center justify-center">
          <Loading withOverlay={false} small />
        </div>
      )}

      {!!adm0Code && !isFetching && (
        <>
          <div style={{ height: "calc(100vh - 240px)", minHeight: "60vh", marginTop: 12 }}>
            <AutoResizer>
              {({ width, height }) => (
                <BaseTable
                  data={adminUnitData?.items ?? []}
                  width={width}
                  height={height}
                  rowKey="adm2Code"
                  className="custom-table"
                  rowHeight={38}
                  headerHeight={45}
                  columns={columns}
                  emptyRenderer={() => <p>No records to display</p>}
                />
              )}
            </AutoResizer>
          </div>

          <Pagination
            totalItems={adminUnitData?.count ?? 0}
            pageSize={pageSize}
            pageSizes={[20, 50, 100]}
            page={page}
            onChange={event => {
              const item = event as unknown as { page: number; pageSize: number }
              setPage(item.page)
              setPageSize(item.pageSize)
            }}
          />
        </>
      )}
    </>
  )
}

export default AdminUnit2
